import React, { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface CalendlyModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title?: string;
}

const CalendlyModal: React.FC<CalendlyModalProps> = ({ 
  isOpen, 
  onClose, 
  url, 
  title = "Schedule Your Free Consultation" 
}) => {
  const [isCalendlyLoaded, setIsCalendlyLoaded] = useState(false);
  const [isWidgetInitialized, setIsWidgetInitialized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Load Calendly CSS if not already loaded
      if (!document.querySelector('link[href*="calendly.com"]')) {
        const link = document.createElement('link');
        link.href = 'https://assets.calendly.com/assets/external/widget.css';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }

      // Load Calendly JS if not already loaded
      if (!document.querySelector('script[src*="calendly.com"]')) {
        const script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        script.onload = () => setIsCalendlyLoaded(true);
        document.head.appendChild(script);
      } else {
        setIsCalendlyLoaded(true);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && isCalendlyLoaded && window.Calendly && !isWidgetInitialized) {
      // Clear any existing widgets first
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      
      // Initialize Calendly inline widget
      (window.Calendly as unknown as { initInlineWidget: (options: { url: string; parentElement: HTMLElement | null; prefill: Record<string, unknown>; utm: Record<string, unknown> }) => void }).initInlineWidget({
        url: url,
        parentElement: containerRef.current,
        prefill: {},
        utm: {}
      });
      
      setIsWidgetInitialized(true);
    }
  }, [isOpen, isCalendlyLoaded, url, isWidgetInitialized]);

  // Cleanup function to remove widgets when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      setIsWidgetInitialized(false);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
                 <motion.div
           initial={{ scale: 0.9, opacity: 0 }}
           animate={{ scale: 1, opacity: 1 }}
           exit={{ scale: 0.9, opacity: 0 }}
           className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full h-[95vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}
         >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

                     {/* Calendly Container */}
           <div className="flex-1">
                                                    <div 
                 ref={containerRef}
                 key={`calendly-${url}`}
                 id="calendly-container"
                 className="w-full h-full"
                 style={{ minHeight: 'calc(100vh - 200px)' }}
               >
                             {!isCalendlyLoaded && (
                 <div className="flex items-center justify-center h-full">
                   <div className="text-center">
                     <div className="w-16 h-16 mx-auto mb-4 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                     <p className="text-lg font-medium text-gray-600">Loading calendar...</p>
                   </div>
                 </div>
               )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CalendlyModal;
